import type {
  CustomComment3,
  CustomCommentResponse,
  FormattedCustomComment,
  Social,
  SocialSortReviews,
  SocialUpdate,
} from '~/types'

export const useSocialContestStore = defineStore(
  'useSocialContestStore',
  () => {
    // STATE
    const socialPost = ref<Social>({
      slug: '',
      slugType: '',
      likes_count: 0,
      is_liked: false,
      shares_count: 0,
      comments_count: 0,
      comments: [],
      tempComments: [],
      commentPage: 1,
      has_more_comments: false,
      is_following: false,
      sortComments: 'popularity',
    })

    // GETTERS
    const getSlug = computed(() => socialPost.value.slug)
    const getSortComments = computed(() => socialPost.value.sortComments)
    const getSlugTypeUrl = computed(() =>
      socialPost.value.slugType === 'wallContestPost' ? 'wcPost' : 'scTeam',
    )
    const getCommentPage = computed(() => socialPost.value.commentPage)
    const getSubCommentPage = (parentId: number) =>
      computed(() => {
        return (
          socialPost.value.comments.find((comment) => comment.id === parentId)
            ?.currentPage ?? 1
        )
      })

    const getPath = <
      Prefix extends '/secure' | '',
      Suffix extends '/comment' | '/like' | '/share',
    >(
      pathPrefix: Prefix,
      pathSuffix: Suffix,
    ) => {
      return `${pathPrefix}/${getSlugTypeUrl.value}/${getSlug.value}${pathSuffix}` as const
    }

    // MUTATIONS
    const setSocialContest = (socialContest: Social) => {
      socialPost.value = { ...socialContest }
    }

    const updateLikeComment = (update: SocialUpdate) => {
      if (update.idParent == null) {
        const indexComment = socialPost.value.comments.findIndex(
          (comment) => comment.id === update.idComment,
        )
        let likes = socialPost.value.comments[indexComment].likes_count ?? 0

        if (update.status) {
          likes++
        } else {
          likes--
        }

        socialPost.value.comments.splice(indexComment, 1, {
          ...socialPost.value.comments[indexComment],
          is_liked: update.status,
          likes_count: likes,
        })
        return
      }
      const indexParent = socialPost.value.comments.findIndex(
        (comment) => comment.id === update.idParent,
      )

      const children = socialPost.value.comments[indexParent].children ?? []

      const commentIndex = children.findIndex(
        (comment) => comment.id === update.idComment,
      )

      let likesCount = children[commentIndex].likes_count ?? 0

      if (update.status) {
        likesCount++
      } else {
        likesCount--
      }

      const commentParent = socialPost.value.comments.find(
        (comment) => comment.id === update.idParent,
      )

      if (commentParent && commentParent.children) {
        commentParent.children.splice(commentIndex, 1, {
          ...commentParent.children[commentIndex],
          is_liked: update.status,
          likes_count: likesCount,
        })
      }
      if (!commentParent) return
      socialPost.value.comments.splice(indexParent, 1, {
        ...commentParent,
      })
    }

    const updateLikeContent = (update: boolean) => {
      let likes = socialPost.value.likes_count

      if (update) {
        likes++
      } else {
        likes--
      }

      socialPost.value = {
        ...socialPost.value,
        is_liked: update,
        likes_count: likes,
      }
    }

    const updateFollowWaper = (update: boolean) => {
      socialPost.value = { ...socialPost.value, is_following: update }
    }

    const updateShareContent = () => {
      socialPost.value = {
        ...socialPost.value,
        shares_count: socialPost.value.shares_count + 1,
      }
    }

    const updateSortComments = (update: { sort: SocialSortReviews }) => {
      socialPost.value.sortComments = update.sort
    }

    const setTempComments = () => {
      socialPost.value.tempComments = [...socialPost.value.comments]
    }

    const refreshComments = (update: {
      comments: FormattedCustomComment[]
      first: boolean
    }) => {
      const temp = [...update.comments]

      const rawSocialPost = unref(socialPost)

      temp.forEach((element, index) => {
        const indCom = rawSocialPost.tempComments?.findIndex(
          (com) => com.id === element.id,
        )

        if (typeof indCom === 'number' && indCom !== -1) {
          temp.splice(index, 1, {
            ...element,
            isOpen: !!rawSocialPost.tempComments?.[indCom].isOpen,
          })
        }
      })

      if (update.first) {
        socialPost.value.comments = [...temp]
      } else {
        socialPost.value.comments.push(...temp)
      }
    }

    const addCommentPage = () => {
      socialPost.value.commentPage = socialPost.value.commentPage + 1
      socialPost.value.comments_count += 1
    }

    const addCommentCount = () => {
      socialPost.value.comments_count += 1
    }

    const updateComments = (update: {
      comments: FormattedCustomComment[]
      has_more_comments: boolean
      newPage: number
    }) => {
      socialPost.value.comments.push(...update.comments)
      socialPost.value.has_more_comments = update.has_more_comments
      socialPost.value.commentPage = update.newPage
    }

    const updateComment = (update: { idComment?: number; status: boolean }) => {
      const indexCom = socialPost.value.comments.findIndex(
        (com) => com.id === update.idComment,
      )

      socialPost.value.comments.splice(indexCom, 1, {
        ...socialPost.value.comments[indexCom],
        isOpen: update.status,
      })
    }

    const updateSubComments = (update: {
      parentId: number
      comments: CustomComment3[]
      has_more_comments: boolean
      newPage: number
    }) => {
      const commentParentIndex = socialPost.value.comments.findIndex(
        (comment) => comment.id === update.parentId,
      )

      const commentParent = socialPost.value.comments[commentParentIndex]

      socialPost.value.comments[commentParentIndex].currentPage = update.newPage
      socialPost.value.comments[commentParentIndex].has_more_comments =
        update.has_more_comments

      if (!commentParent.children) return

      const mergedComments = commentParent.children.concat(update.comments)
      const uniqueComments = mergedComments.filter(
        (comment, index, self) =>
          index === self.findIndex((t) => t.id === comment.id),
      )
      /* 
       // Old implementation, cannot work because indexOf works on object reference
       // and update.comments are new objects coming from the API
       const comments = commentParent.children.concat(update.comments)
       const d = comments.filter((item, index) => comments.indexOf(item) === index)
      */
      socialPost.value.comments[commentParentIndex].children = uniqueComments
    }

    const initSocialContest = () => {
      socialPost.value = {
        slug: '',
        slugType: '',
        likes_count: 0,
        is_liked: false,
        shares_count: 0,
        comments_count: 0,
        comments: [],
        commentPage: 1,
        has_more_comments: false,
        is_following: false,
      }
    }

    const { authenticated } = useAuthenticated()
    // ACTIONS
    const refreshCommentsAction = async () => {
      setTempComments()

      const pathPrefix = authenticated.value ? '/secure' : ''
      const pathSuffix = '/comment' as const
      const path = getPath(pathPrefix, pathSuffix)
      for (let index = 1; index <= getCommentPage.value; index++) {
        const { error, data } = await useCustomFetch<CustomCommentResponse>(
          path,
          {
            query: {
              page: index,
              sort: getSortComments.value,
            },
          },
        )
        const rawData = unref(data)
        if (error.value || !rawData) {
          console.error(
            'from actions',
            'There was a problem show refreshComments: ',
            error,
          )
          continue
        }

        const formattedComments = rawData.results.map((comment) => ({
          ...comment,
          currentPage: 1,
          isOpen: false,
        }))
        refreshComments({
          comments: formattedComments,
          first: index === 1,
        })
      }
    }

    const showMoreContent = async ({
      parentId,
    }: {
      parentId?: number
    } = {}) => {
      const pathPrefix = authenticated.value ? '/secure' : ''
      const pathSuffix = '/comment' as const

      const path = getPath(pathPrefix, pathSuffix)
      if (!path) return

      if (parentId) {
        const { error, data } = await useCustomFetch<CustomCommentResponse>(
          path,
          {
            query: {
              page: getSubCommentPage(parentId).value + 1,
              parent_id: parentId,
              sort: getSortComments.value,
            },
          },
        )

        const rawData = unref(data)
        if (error.value || !rawData) {
          return logError(
            'from actions',
            'There was a problem show more comments: ',
            error,
          )
        }

        return updateSubComments({
          parentId,
          comments: rawData.results,
          has_more_comments: rawData.has_more_comments ?? false,
          newPage: getSubCommentPage(parentId).value + 1,
        })
      }

      const { error, data } = await useCustomFetch<CustomCommentResponse>(
        path,
        {
          query: {
            page: getCommentPage.value + 1,
            sort: getSortComments.value,
          },
        },
      )

      const rawData = unref(data)

      if (error.value || !rawData) {
        return logError(
          'from actions',
          'There was a problem show more comments: ',
          error,
        )
      }

      const formattedComments = rawData.results.map((comment) => ({
        ...comment,
        currentPage: 1,
        isOpen: false,
      }))

      updateComments({
        comments: formattedComments,
        has_more_comments: rawData.has_more_comments ?? false,
        newPage: getCommentPage.value + 1,
      })
    }

    type ContentImageKey = `contentImage${number}`

    const postCommentContent = async ({
      content,
    }: {
      content: {
        content: string
        parent?: string
        [key: ContentImageKey]: string | undefined
      }
    }) => {
      const path = getPath('/secure', '/comment')

      if (!path) return {}
      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        body: content,
        method: 'POST',
      })

      if (error.value || !data.value) {
        console.error(
          'from actions',
          'There was a problem commenting content: ',
          error,
        )
        return { error: error.value, data: data.value }
      }

      if (!content.parent && getCommentPage.value > 1) {
        addCommentPage()
      }

      addCommentCount()
      refreshCommentsAction()
      return { error: error.value, data: data.value }
    }

    const commentContent = async ({
      content,
    }: {
      content: {
        content: string
        contentEmbed?: string
        parent?: string
        [key: string]: string | undefined
      }
    }) => {
      const { error, data } = await useCustomFetch<{ message: string }>(
        `/secure/${getSlugTypeUrl.value}/${getSlug.value}/comment`,
        {
          body: content,
          method: 'POST',
        },
      )

      if (error.value) {
        console.error(
          'from actions',
          'There was a problem liking content: ',
          error.value,
        )
        return { error: error.value, data: data.value }
      }

      if (!content.parent && getCommentPage.value > 1) {
        addCommentPage()
      }

      addCommentCount()
      refreshCommentsAction()
      return { error: error.value, data: data.value }
    }

    const shareContent = async () => {
      track('social', {
        interaction: 'share',
        target_url: getSlug.value,
      })

      const pathPrefix = '/secure'
      const pathSuffix = '/share'
      const path = getPath(pathPrefix, pathSuffix)

      if (!path) return

      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        method: 'POST',
      })

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem sharing content: ',
          error,
        )
      }

      updateShareContent()
    }

    const followWaper = async (slugWaper: string) => {
      const { error, data } = await useCustomFetch<{ message: string }>(
        `${API.POST__FOLLOW_WAPER}/${slugWaper}`,
        { method: 'POST' },
      )

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem follow waper: ',
          error,
        )
      }

      updateFollowWaper(true)
    }

    const unfollowWaper = async (slugWaper: string) => {
      const { error, data } = await useCustomFetch<{ message: string }>(
        `${API.POST__UNFOLLOW_WAPER}/${slugWaper}`,
        { method: 'POST' },
      )

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem unfollow waper: ',
          error,
        )
      }

      updateFollowWaper(false)
    }

    const likeWcPost = async (slug: string) => {
      track('social', {
        interaction: 'like',
        target_url: slug,
      })

      const path = `/secure/wcPost/${slug}/like`

      if (!path) return

      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        method: 'POST',
      })

      if (error.value || !data.value) {
        console.error(
          'from actions',
          'There was a problem liking content: ',
          error,
        )
      }
    }

    const unlikeWcPost = async (slug: string) => {
      track('social', {
        interaction: 'unlike',
        target_url: slug,
      })

      const path = `/secure/wcPost/${slug}/like`

      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        method: 'DELETE',
      })
      if (error.value || !data.value) {
        console.error(
          'from actions',
          'There was a problem unliking content: ',
          error,
        )
      }
    }

    const likeContent = async () => {
      track('social', {
        interaction: 'like',
        target_url: getSlug.value,
      })

      const pathPrefix = '/secure' as const
      const pathSuffix = '/like' as const
      const path = getPath(pathPrefix, pathSuffix)

      if (!path) return

      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        method: 'POST',
      })

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem liking content: ',
          error,
        )
      }

      updateLikeContent(true)
    }

    const unlikeContent = async () => {
      const pathPrefix = '/secure' as const
      const pathSuffix = `/like` as const

      const path = getPath(pathPrefix, pathSuffix)
      if (!path) return

      const { error, data } = await useCustomFetch<{ message: string }>(path, {
        method: 'DELETE',
      })

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem unliking content: ',
          error,
        )
      }

      updateLikeContent(false)
    }

    const unlikeComment = async ({
      idComment,
      idParent,
    }: {
      idComment?: number
      idParent?: number
    }) => {
      const { error, data } = await useCustomFetch<{ message: string }>(
        `/secure/comment/${idComment}/like`,
        { method: 'DELETE' },
      )

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem unliking comment: ',
          error,
        )
      }

      updateLikeComment({
        idComment,
        idParent,
        status: false,
      })
    }

    const likeComment = async ({
      idComment,
      idParent,
    }: {
      idComment?: number
      idParent?: number
    }) => {
      const { error, data } = await useCustomFetch<{ message: string }>(
        `/secure/comment/${idComment}/like`,
        { method: 'POST' },
      )

      if (error.value || !data.value) {
        return logError(
          'from actions',
          'There was a problem liking comment: ',
          error,
        )
      }

      updateLikeComment({
        idComment,
        idParent,
        status: true,
      })
    }

    const changeStatusComment = updateComment
    const updateSortCommentsAction = updateSortComments
    const fetchSocialContest = setSocialContest

    return {
      // STATE
      social: socialPost,

      // GETTERS
      getSubCommentPage,
      getSlugTypeUrl,

      // MUTATIONS
      setSocialContest,
      updateLikeContent,
      updateFollowWaper,
      updateShareContent,
      updateSortComments,
      setTempComments,
      refreshComments,
      addCommentPage,
      addCommentCount,
      updateComments,
      updateComment,
      updateSubComments,
      initSocialContest,
      fetchSocialContest,

      // ACTIONS
      updateLikeComment,
      refreshCommentsAction,
      showMoreContent,
      commentContent,
      shareContent,
      followWaper,
      unfollowWaper,
      likeWcPost,
      unlikeWcPost,
      likeContent,
      unlikeContent,
      unlikeComment,
      likeComment,
      changeStatusComment,
      updateSortCommentsAction,
      postCommentContent,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useSocialContestStore, import.meta.hot),
  )
}
